import { Children, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';
import { getBranchOptions, getCommercialDashboard } from '@/features/operations/api';
import { deviceStateLabels, originLabels } from '@/features/operations/labels';
import { getQuotationStatusLabel } from '@/features/operations/quotationStatus';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import type {
  BranchOption,
  CommercialDashboard,
  CommercialDashboardFilters,
} from '@/features/operations/types';

const DAY_MS = 24 * 60 * 60 * 1000;

function defaultPeriod(): CommercialDashboardFilters {
  const dateTo = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montevideo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const end = new Date(`${dateTo}T00:00:00.000Z`);
  return {
    dateFrom: new Date(end.getTime() - 29 * DAY_MS).toISOString().slice(0, 10),
    dateTo,
  };
}

function sameFilters(left: CommercialDashboardFilters, right: CommercialDashboardFilters) {
  return (
    left.dateFrom === right.dateFrom &&
    left.dateTo === right.dateTo &&
    (left.branchId ?? '') === (right.branchId ?? '')
  );
}

function formatCalendarDate(value: string) {
  return new Date(`${value}T00:00:00.000Z`).toLocaleDateString('es-UY', {
    dateStyle: 'short',
    timeZone: 'UTC',
  });
}

function describeFilters(filters: CommercialDashboardFilters, branches: BranchOption[]) {
  const branch = filters.branchId
    ? (branches.find(({ id }) => id === filters.branchId)?.name ?? 'Sucursal seleccionada')
    : 'Todas las sucursales';
  return `${formatCalendarDate(filters.dateFrom)} al ${formatCalendarDate(filters.dateTo)} · ${branch}`;
}

function describeDifference(
  differenceMinor: number,
  formatMoney: (amount: number, currency: string) => string,
) {
  if (differenceMinor === 0) return 'Sin diferencia contra lo publicado';
  return `${formatMoney(Math.abs(differenceMinor), 'USD')} ${
    differenceMinor < 0 ? 'por debajo' : 'por encima'
  } de lo publicado`;
}

function Metric({ label, value, help }: { label: string; value: string | number; help?: string }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {help ? (
        <Typography variant="caption" color="text.secondary">
          {help}
        </Typography>
      ) : null}
    </Box>
  );
}

function MetricGrid({ children }: { children: ReactNode }) {
  return (
    <Grid container spacing={3}>
      {Children.map(children, (child) => (
        <Grid item xs={12} sm={6} md={3}>
          {child}
        </Grid>
      ))}
    </Grid>
  );
}

export function CommercialDashboardPage() {
  const { token } = useAuth();
  const { formatMoney } = usePriceDisplay();
  const initial = useMemo(defaultPeriod, []);
  const [draft, setDraft] = useState<CommercialDashboardFilters>(initial);
  const [applied, setApplied] = useState<CommercialDashboardFilters>(initial);
  const [data, setData] = useState<CommercialDashboard | null>(null);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoaded = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ dateFrom?: string; dateTo?: string }>({});

  useEffect(() => {
    if (!token) return;
    let active = true;
    getBranchOptions(token)
      .then((items) => active && setBranches(items))
      .catch(() => active && setError('No pudimos cargar las sucursales disponibles.'));
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    if (hasLoaded.current) setRefreshing(true);
    else setLoading(true);
    setError(null);
    getCommercialDashboard(token, applied)
      .then((result) => {
        if (active) {
          setData(result);
          hasLoaded.current = true;
        }
      })
      .catch(
        () =>
          active &&
          setError('No pudimos cargar los indicadores. Revisá los filtros e intentá de nuevo.'),
      )
      .finally(() => {
        if (active) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => {
      active = false;
    };
  }, [token, applied]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const errors: typeof fieldErrors = {};
    if (!draft.dateFrom) errors.dateFrom = 'Elegí la fecha inicial.';
    if (!draft.dateTo) errors.dateTo = 'Elegí la fecha final.';
    if (draft.dateFrom && draft.dateTo && draft.dateFrom > draft.dateTo) {
      errors.dateFrom = 'La fecha inicial no puede ser posterior a la final.';
      errors.dateTo = 'La fecha final no puede ser anterior a la inicial.';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setApplied({ ...draft, branchId: draft.branchId || undefined });
  };

  const rate = data
    ? `${(data.quotations.decisions.approvalRate * 100).toLocaleString('es-UY', { maximumFractionDigits: 1 })} %`
    : '0 %';
  const difference = data?.sales.differenceFromPublishedMinor ?? 0;
  const formattedDifference = describeDifference(difference, formatMoney);
  const noActivity = data
    ? data.quotations.total +
        data.devices.total +
        data.sales.count +
        data.reservations.active +
        data.reservations.expired +
        data.reservations.completed +
        data.accounts.total ===
      0
    : false;
  const displayedFilters = data?.period;
  const filtersDifferFromResult = displayedFilters ? !sameFilters(draft, displayedFilters) : false;
  const hasUnappliedChanges = displayedFilters
    ? !sameFilters(draft, applied) && filtersDifferFromResult
    : false;
  const failedToApplyFilters = displayedFilters
    ? Boolean(error) && !sameFilters(applied, displayedFilters)
    : false;

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="DIRECCIÓN"
        title="Dashboard comercial"
        description="Resultados comerciales derivados de la operación registrada en PhoneTec."
        badge="Solo administración"
      />

      <SectionCard
        title="Período y sucursal"
        description="El período inicial comprende los últimos 30 días, incluido hoy."
      >
        <Box component="form" onSubmit={submit} noValidate>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                value={draft.dateFrom}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, dateFrom: event.target.value }))
                }
                error={Boolean(fieldErrors.dateFrom)}
                helperText={fieldErrors.dateFrom ?? 'Fecha del evento que inicia el período.'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                value={draft.dateTo}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, dateTo: event.target.value }))
                }
                error={Boolean(fieldErrors.dateTo)}
                helperText={fieldErrors.dateTo ?? 'Fecha del evento que cierra el período.'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Sucursal"
                value={draft.branchId ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, branchId: event.target.value }))
                }
                helperText="No cambia las métricas de cuentas."
              >
                <MenuItem value="">Todas las sucursales</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                    {branch.isActive ? '' : ' (inactiva)'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <PrimaryButton type="submit" fullWidth disabled={refreshing} sx={{ minHeight: 56 }}>
                {refreshing ? 'Actualizando...' : 'Actualizar indicadores'}
              </PrimaryButton>
            </Grid>
          </Grid>
        </Box>
        <Alert severity="info">
          La sucursal se aplica a cotizaciones recibidas, equipos, reservas y ventas con una
          ubicación registrada. Las cuentas nuevas y sus activaciones siempre son globales.
        </Alert>
      </SectionCard>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : data ? (
        <Stack spacing={3}>
          <Alert severity="info">
            <strong>Resultados aplicados:</strong> {describeFilters(data.period, branches)}.
          </Alert>
          {filtersDifferFromResult ? (
            <Alert severity={failedToApplyFilters ? 'warning' : 'info'}>
              {refreshing
                ? `Estamos actualizando los indicadores. Mientras tanto, ves los resultados de ${describeFilters(data.period, branches)}.`
                : hasUnappliedChanges
                  ? `Tenés cambios sin aplicar. Los indicadores siguen correspondiendo a ${describeFilters(data.period, branches)}.`
                  : `La última actualización no se completó. Los indicadores visibles siguen correspondiendo a ${describeFilters(data.period, branches)}.`}
            </Alert>
          ) : null}
          {noActivity ? (
            <Alert severity="info">
              No hubo actividad registrada para estos filtros. Los indicadores se muestran en cero.
            </Alert>
          ) : null}

          <SectionCard
            title="Cotizaciones"
            description="Solicitudes creadas y decisiones tomadas dentro del período."
          >
            <MetricGrid>
              <Metric label="Cotizaciones creadas" value={data.quotations.total} />
              <Metric label="Aprobaciones" value={data.quotations.decisions.approved} />
              <Metric label="Rechazos" value={data.quotations.decisions.rejected} />
              <Metric
                label="Tasa de aprobación"
                value={rate}
                help="Aprobadas sobre decisiones tomadas."
              />
            </MetricGrid>
            <Divider />
            <MetricGrid>
              {Object.entries(data.quotations.byStatus).map(([status, count]) => (
                <Metric
                  key={status}
                  label={`Estado actual: ${getQuotationStatusLabel(status)}`}
                  value={count}
                />
              ))}
              {Object.entries(data.quotations.byOrigin).map(([origin, count]) => (
                <Metric key={origin} label={`Origen: ${originLabels[origin]}`} value={count} />
              ))}
            </MetricGrid>
          </SectionCard>

          <SectionCard
            title="Equipos"
            description="Equipos ingresados en el período y tiempo de venta de los vendidos."
          >
            <MetricGrid>
              <Metric label="Equipos ingresados" value={data.devices.total} />
              <Metric
                label="Promedio publicado a vendido"
                value={`${data.devices.averageDaysPublishedToSold.toLocaleString('es-UY', { maximumFractionDigits: 1 })} días`}
              />
              {Object.entries(data.devices.byState).map(([state, count]) => (
                <Metric key={state} label={deviceStateLabels[state]} value={count} />
              ))}
            </MetricGrid>
            {data.devices.byBranch.length ? (
              <>
                <Divider />
                <Typography variant="subtitle2">Equipos por sucursal</Typography>
                <MetricGrid>
                  {data.devices.byBranch.map((branch) => (
                    <Metric key={branch.branchId} label={branch.branchName} value={branch.count} />
                  ))}
                </MetricGrid>
              </>
            ) : null}
          </SectionCard>

          <SectionCard
            title="Reservas y ventas"
            description="Cada resultado cuenta la fecha de su propio evento dentro del período."
          >
            <MetricGrid>
              <Metric label="Reservas activas creadas" value={data.reservations.active} />
              <Metric label="Reservas vencidas" value={data.reservations.expired} />
              <Metric label="Reservas concretadas" value={data.reservations.completed} />
              <Metric label="Ventas" value={data.sales.count} />
              <Metric label="Monto vendido" value={formatMoney(data.sales.amountMinor, 'USD')} />
              <Metric
                label="Diferencia contra precio publicado"
                value={formattedDifference}
                help="Suma de precio final menos precio publicado."
              />
            </MetricGrid>
          </SectionCard>

          <SectionCard
            title="Cuentas de clientes"
            description="Estas métricas son globales y no cambian al filtrar por sucursal."
          >
            <MetricGrid>
              <Metric label="Cuentas nuevas" value={data.accounts.total} />
              <Metric label="Registro web" value={data.accounts.web} />
              <Metric label="Creadas en mostrador" value={data.accounts.counter} />
              <Metric label="Activaciones de mostrador" value={data.accounts.counterActivated} />
            </MetricGrid>
          </SectionCard>
        </Stack>
      ) : null}
    </Stack>
  );
}
